import { format, parseISO, type Locale } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import {
  DATE_DISPLAY_PATTERNS,
  DEFAULT_DATE_FORMAT_REGION,
  MONTH_FIRST_DATE_FORMAT_REGION_SET,
} from '@config/dateFormatConfig';
import {
  LOCALIZATION_DEFAULTS,
  SUPPORTED_DATE_PATTERNS,
  getLocalizationConfig,
  type SupportedDatePattern,
} from '@config/localizationConfig';
import { emitLocalizationWarning } from '@utils/localizationWarnings';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;

export type DateDisplayPattern = (typeof DATE_DISPLAY_PATTERNS)[keyof typeof DATE_DISPLAY_PATTERNS];

function padDatePart(value: number): string {
  return String(value).padStart(2, '0');
}

export function getDateDisplayPattern(
  regionName: string = DEFAULT_DATE_FORMAT_REGION
): DateDisplayPattern {
  const normalizedRegionName = regionName.trim().toUpperCase();
  return MONTH_FIRST_DATE_FORMAT_REGION_SET.has(normalizedRegionName)
    ? DATE_DISPLAY_PATTERNS.monthFirst
    : DATE_DISPLAY_PATTERNS.dayFirst;
}

export function formatDateForDisplay(
  date: Date | undefined,
  pattern: DateDisplayPattern = DATE_DISPLAY_PATTERNS.dayFirst
): string {
  if (!date) return '';

  const day = padDatePart(date.getDate());
  const month = padDatePart(date.getMonth() + 1);
  const year = date.getFullYear();

  if (pattern === DATE_DISPLAY_PATTERNS.monthFirst) {
    return `${month}/${day}/${year}`;
  }

  return `${day}/${month}/${year}`;
}

export function formatDateForRegion(
  date: Date | undefined,
  regionName: string = DEFAULT_DATE_FORMAT_REGION
): string {
  return formatDateForDisplay(date, getDateDisplayPattern(regionName));
}

export function formatIsoDateForRegion(
  isoDate: string | undefined,
  regionName: string = DEFAULT_DATE_FORMAT_REGION
): string {
  if (!isoDate || isoDate.length < 10 || !DATE_PATTERN.test(isoDate)) return '';

  const parts = isoDate.slice(0, 10).split('-').map(Number);
  const year = parts[0] ?? Number.NaN;
  const month = parts[1] ?? Number.NaN;
  const day = parts[2] ?? Number.NaN;

  if ([year, month, day].some((value) => Number.isNaN(value))) {
    return '';
  }

  return formatDateForDisplay(new Date(year, month - 1, day), getDateDisplayPattern(regionName));
}

/**
 * Calculate the number of days between a given ISO date string and today (or a reference date).
 * Used for deterministic age calculations in tests and display.
 * @param isoDate ISO date string (YYYY-MM-DD format)
 * @param referenceDate Optional reference date for testing. Defaults to today.
 * @returns Number of days since the given date
 */
export function calculateAgeDays(isoDate: string, referenceDate?: Date): number {
  if (!isoDate || isoDate.length < 10 || !DATE_PATTERN.test(isoDate)) return 0;

  const submittedDate = new Date(isoDate + 'T00:00:00Z');
  const now = referenceDate ? new Date(referenceDate) : new Date();

  if (isNaN(submittedDate.getTime())) return 0;

  const diffMs = now.getTime() - submittedDate.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
}

// ---------------------------------------------------------------------------
// Centralised date formatter (feature 105)
// ---------------------------------------------------------------------------

interface PatternMapping {
  readonly token: string;
  readonly locale?: Locale;
}

// Internal — MUST NOT be exported (date-formatter contract).
const PATTERN_TO_DATEFNS_TOKEN: Readonly<Record<SupportedDatePattern, PatternMapping>> = {
  'MM/DD/YYYY': { token: 'MM/dd/yyyy' },
  'DD/MM/YYYY': { token: 'dd/MM/yyyy' },
  'MMM Do, YYYY': { token: 'MMM do, yyyy', locale: enUS },
  'MMM DD, YYYY': { token: 'MMM dd, yyyy', locale: enUS },
};

const emittedInvalidPatterns = new Set<string>();

/**
 * Test-only helper used by `dateFormat.test.ts` to reset the once-per-process
 * INVALID_DATE_PATTERN dedup set between cases.
 */
export function __resetFormatDatePatternDedupForTests(): void {
  emittedInvalidPatterns.clear();
}

function coerceToDate(value: Date | string | number | null | undefined): Date | null {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  if (typeof value === 'string') {
    if (value.length === 0) return null;
    const parsed = parseISO(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

function resolvePattern(pattern?: SupportedDatePattern): SupportedDatePattern {
  if (pattern === undefined) {
    return getLocalizationConfig().dateFormatPattern;
  }
  if (!(SUPPORTED_DATE_PATTERNS as readonly string[]).includes(pattern)) {
    const asString = String(pattern);
    if (!emittedInvalidPatterns.has(asString)) {
      emittedInvalidPatterns.add(asString);
      emitLocalizationWarning({
        code: 'LOCALIZATION_INVALID_DATE_PATTERN',
        configured: asString,
        fallback: LOCALIZATION_DEFAULTS.dateFormatPattern,
      });
    }
    return LOCALIZATION_DEFAULTS.dateFormatPattern;
  }
  return pattern;
}

/**
 * Format a date using the central, configuration-driven pattern path.
 *
 * - Returns `''` for null/undefined/invalid inputs (silent — no warning).
 * - When `pattern` is omitted, reads the active pattern from
 *   `getLocalizationConfig().dateFormatPattern`.
 * - For an explicit `pattern` that is not in `SUPPORTED_DATE_PATTERNS`,
 *   falls back to `LOCALIZATION_DEFAULTS.dateFormatPattern` and emits
 *   `LOCALIZATION_INVALID_DATE_PATTERN` once per unique value per process.
 */
export function formatDate(
  date: Date | string | number | null | undefined,
  pattern?: SupportedDatePattern
): string {
  const resolved = coerceToDate(date);
  if (!resolved) return '';

  const effectivePattern = resolvePattern(pattern);
  const mapping = PATTERN_TO_DATEFNS_TOKEN[effectivePattern];

  if (mapping.locale) {
    return format(resolved, mapping.token, { locale: mapping.locale });
  }
  return format(resolved, mapping.token);
}

/** Format a 24-hour "HH:mm" string to 12-hour "h:mm AM/PM". Returns the input unchanged if it cannot be parsed. */
export function formatTime12Hour(time: string | undefined | null): string {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  if (h === undefined || m === undefined || Number.isNaN(h) || Number.isNaN(m)) return time;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}
